import { NavLink } from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCirclOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PropTypes from 'prop-types';
import { Component, default as React } from 'react';
import GS from '../../grafsync/grafsync';
import CancelButton from '../CancelButton';
import Errored from '../Errored';
import KebabMenu from '../KebabMenu';
import Loading from '../Loading';
import PrimaryButton from '../PrimaryButton';
import { UserConsumer } from '../providers/UserProvider';
import { globalStyles } from '../style/gstheme';
import { PACKAGES, STATUS_ACCEPTED, STATUS_PENDING, STATUS_REJECTED } from '../utils/constants';
import { makeCancelable, sortByProp } from '../utils/utils';

const styles = () => {
  return Object.assign({
    invited: {
      marginTop: 10,
      flex: '0 0',
      display: 'flex',
      justifyContent: 'flex-end'
    },
    invitationWarning: {
      color: '#CA4F0C',
      fontSize: '14px',
      marginTop: 6
    },
    invitationCount: {
      backgroundColor: '#CA4F0C',
      borderRadius: '50%',
      color: 'white',
      marginLeft: 6,
      fontSize: '14px',
      display: 'inline-block',
      paddingTop: '2px',
      width: '20px',
      textAlign: 'center',
      verticalAlign: 'middle',
      height: '16px'
    },
    invitationAccepted: {
      fontSize: '14px',
      marginTop: 6
    }
  }, globalStyles);
};

const CHECK_TIME = 2000;

class InviteHandler extends Component {

  constructor(props) {
    super(props);
    const rejections = props.rejectedInvitations.reduce(function (map, invitation) {
      map[invitation] = STATUS_REJECTED;
      return map;
    }, {});
    this.state = {
      isLoading: true,
      invitations: [],
      answeredInvitations: rejections,
      hasAccepted: null,
      page: 1,
      pageSize: 8,
      isErrored: null
    };
    this.cancellable = null;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
  }

  checkStatus = async () => {
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
    await this.getInvitations();
    const acceptedInvite = this.state.hasAccepted;
    const invite = this.state.invitations.find(invitation => invitation.id === acceptedInvite);
    if (invite && invite.status[0] !== STATUS_PENDING) {
      this.setState({ hasAccepted: null });
    }
    else {
      this.hcTimeout = setTimeout(() => { this.checkStatus(); }, CHECK_TIME);
    }
  };

  componentDidMount() {
    this.getInvitations();
  }

  getInvitations = async (page = 1) => {
    this.setState(
      { isLoading: true },
      () => {
        this.doGetInvitations()
          .promise
          .then(data => {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            if (data) {
              const invitationData = sortByProp([...data], 'subscriptionName');
              this.setState({ invitations: invitationData, isLoading: false });
            }
            else {
              this.setState({
                isLoading: false
              });
            }
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      });
  };

  doGetInvitations() {
    this.cancellable = makeCancelable(GS.invitations());
    return this.cancellable;
  }

  doAnswerInvitation(invitationIRI, status) {
    this.cancellable = makeCancelable(GS.respondToInvitation(invitationIRI, status));
    return this.cancellable;
  }

  answerInvitation = async (invitationIRI, status) => {
    try {
      await this.doAnswerInvitation(invitationIRI, status).promise;
      const updatedAnsweredInvitations = Object.assign({}, this.state.answeredInvitations);
      updatedAnsweredInvitations[invitationIRI] = status;
      this.setState({ answeredInvitations: updatedAnsweredInvitations });
      if (status === STATUS_ACCEPTED) {
        this.setState({ hasAccepted: invitationIRI }, () => {
          this.hcTimeout = setTimeout(() => { this.checkStatus() ;}, CHECK_TIME);
        });
      }
    }
    catch (e) {
      const message = e.message === "Invitation response is invalid." ? "This invitation may have been withdrawn." : e.message;
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: { message } });
    }
  };

  clearError = async () => {
    await this.getInvitations();
    this.setState({ isErrored: false });
  };

  render() {

    const { classes, allowReject } = this.props;
    const { isLoading, invitations, answeredInvitations, isErrored, hasAccepted } = this.state;
    const outstandingInviteCount = invitations
      .filter(invitation => invitation.status[0] === STATUS_PENDING && !answeredInvitations.hasOwnProperty(invitation.id)).length;

    if (isErrored) return <Errored data={isErrored} clearError={this.clearError} />;
    if (isLoading) return <Loading />;

    return (
      <UserConsumer>
        {({ setSubscription, clearSubscription, rejectInvitation, activeSubscription }) =>
          <div>
            {invitations && invitations.length > 0 &&
              <div style={{ clear: 'both', paddingTop: 20, paddingBottom: 40 }}>
                <Typography variant="h2" className={classes.subMainHeading}>
                  <span>Invites</span>
                  {outstandingInviteCount > 0 && <span className={classes.invitationCount}>{outstandingInviteCount}</span>}
                </Typography>
                {invitations
                  .filter(invitation => !(answeredInvitations.hasOwnProperty(invitation.id)
                    && answeredInvitations[invitation.id] === STATUS_REJECTED))
                  .map(invitation => {
                    const inviter = invitation.givenName ? invitation.givenName + " " + invitation.familyName : null;
                    const active = invitation.subscription === activeSubscription;
                    return <div key={invitation.id} data-test={invitation.subscription}>
                      <div data-test={"subscription-" + invitation.subscriptionName}>
                        <Paper
                          data-test={"invitation-card"}
                          className={active ?
                            classes.activeSubscriptionCard
                            :
                            classes.subscriptionCard
                          }
                          key={invitation.id}
                          style={{
                            ...(invitation.status[0] === STATUS_PENDING
                              && answeredInvitations.hasOwnProperty(invitation.id)) && { backgroundColor: '#f5f5f5' },
                            cursor: invitation.status[0] === STATUS_ACCEPTED ? 'pointer' : 'inherit'
                          }}
                          onClick={() => invitation.status[0] === STATUS_ACCEPTED && !active ?
                            setSubscription(invitation.subscription, true)
                            :
                            null
                          }
                        >
                          <div style={{ display: 'flex' }}>
                            <Typography className={classes.subHeadingLarge} style={{ flex: 1, ...(active) && { color: 'white' } }}>
                              {invitation.subscriptionName}
                            </Typography>
                            {allowReject && invitation.status[0] === STATUS_ACCEPTED &&
                              <div style={{ flex: '0 0', alignSelf: 'right', transform: 'translatey(-5px)' }}>
                                <KebabMenu items={["Reject"]}
                                  colour={active ? "white" : null}
                                  onSelect={(index) => {
                                    if (index !== null) {
                                      this.answerInvitation(invitation.id, STATUS_REJECTED);
                                      if (active) {
                                        clearSubscription(invitation.id);
                                      }
                                      else {
                                        rejectInvitation(invitation.id);
                                      }
                                    }
                                  }} />
                              </div>
                            }
                          </div>
                          <Typography variant="body2" style={{ marginTop: 6, ...(active) && { color: 'white' } }}>
                            {PACKAGES[invitation.subscriptionPackage]}
                          </Typography>
                          {invitation.status[0] === STATUS_PENDING ?
                            answeredInvitations.hasOwnProperty(invitation.id) ?
                              <div style={{ flex: 1 }}>
                                <Typography className={classes.invitationWarning}>
                                  <span>Invite</span>
                                  {inviter && <span> from {inviter}</span>}
                                  <span> accepted</span>
                                </Typography>
                                <Typography className={classes.invitationWarning}>
                                  <span>Invitation will be available to select shortly</span>
                                </Typography>
                              </div>
                              :
                              !hasAccepted &&
                              <React.Fragment>
                                <Typography style={{ flex: 1 }} className={classes.invitationWarning}>
                                  <span>Accept or reject invite</span>
                                  {inviter && <span> from {inviter}</span>}
                                </Typography>
                                <div className={classes.invited}>
                                  <div style={{ marginRight: 6 }}>
                                    <CancelButton onClick={() => this.answerInvitation(invitation.id, STATUS_REJECTED)}>Reject</CancelButton>
                                  </div>
                                  <PrimaryButton onClick={() => this.answerInvitation(invitation.id, STATUS_ACCEPTED)}>Accept</PrimaryButton>
                                </div>
                              </React.Fragment>
                            :
                            null
                          }
                          {invitation.status[0] === STATUS_ACCEPTED &&
                            <React.Fragment>
                              <Typography style={{ flex: 1 }} className={classes.invitationAccepted}>
                                {inviter && <span>Invited by {inviter}</span>}
                              </Typography>
                              {active &&
                                <Typography variant="body2" style={{ marginTop: 10 }}><CheckCirclOutlineIcon style={{ verticalAlign: 'bottom' }} /> Active</Typography>
                              }
                              {(active || this.props.addLinks) &&
                                <div style={{ textAlign: 'right' }}>
                                  <IconButton
                                    data-test="go-to-active"
                                    size="small"
                                    style={{ width: 40, height: 40, verticalAlign: 'middle', paddingTop: 6 }}>
                                    <NavLink to={"/"} className={classes.cardLink}>
                                      <ArrowForwardIcon className={classes.actionButton} />
                                    </NavLink>
                                  </IconButton>
                                </div>
                              }
                            </React.Fragment>
                          }
                        </Paper>
                      </div>
                    </div>;
                  }
                  )}
              </div>
            }
          </div>
        }
      </UserConsumer>
    );
  }
}

InviteHandler.defaultProps = {
  allowReject: true,
  addLinks: false,
  rejectedInvitations: []
};

InviteHandler.propTypes = {
  classes: PropTypes.object,
  rejectedInvitations: PropTypes.array,
  allowReject: PropTypes.bool,
  addLinks: PropTypes.bool
};

export default withStyles(styles)(InviteHandler);